'use client';

import * as React from 'react';

/**
 * Onda de partículas — a animação-assinatura do MakeStudio (a "voz" virando forma).
 * Um campo de pontos que ondula em tempo real, no gradiente da marca
 * (roxo → ciano → laranja). Canvas + requestAnimationFrame, leve. Identidade
 * própria — não o ciano do LiveKit.
 */
export function ParticleWave({
  height = 320,
  level,
  state,
}: {
  height?: number;
  /** 0..1 — amplitude da voz da Adriana; faz a onda PULSAR. Omitido = calmo. */
  level?: number;
  state?: 'idle' | 'listening' | 'speaking';
}) {
  const ref = React.useRef<HTMLCanvasElement>(null);
  // refs lidas a cada frame (sem reiniciar o RAF quando o level muda).
  const levelRef = React.useRef(0);
  const stateRef = React.useRef<'idle' | 'listening' | 'speaking'>('idle');
  levelRef.current = level ?? 0.08;
  stateRef.current = state ?? 'idle';

  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let t = 0;
    let W = 0;
    let H = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const lerp = (a: number, b: number, m: number) => a + (b - a) * m;
    // gradiente da marca: roxo → ciano → laranja
    const STOPS = [
      [154, 139, 255],
      [90, 200, 255],
      [255, 138, 61],
    ];
    const color = (m: number) => {
      const seg = m < 0.5 ? 0 : 1;
      const lm = m < 0.5 ? m * 2 : (m - 0.5) * 2;
      const a = STOPS[seg];
      const b = STOPS[seg + 1];
      return `rgb(${lerp(a[0], b[0], lm) | 0}, ${lerp(a[1], b[1], lm) | 0}, ${lerp(a[2], b[2], lm) | 0})`;
    };

    // Suaviza o level pra a pulsação não tremer (ataque rápido, decay lento).
    let lv = 0.08;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const target = levelRef.current;
      lv += (target - lv) * (target > lv ? 0.35 : 0.08);
      t += 0.012 + lv * 0.02; // fala mais rápido quando ela "fala"
      const cols = Math.max(40, Math.floor(W / 9));
      const rows = 16;
      const midY = H * 0.52;
      const amp = 20 + lv * 78; // PULSO: amplitude cresce com a voz
      const band = 72 + lv * 38;
      for (let i = 0; i < cols; i++) {
        const nx = i / (cols - 1);
        const x = nx * W;
        const env = Math.sin(nx * Math.PI); // 0 nas pontas, 1 no centro
        if (env <= 0.01) continue;
        for (let j = 0; j < rows; j++) {
          const spread = j / (rows - 1) - 0.5; // -0.5..0.5
          const phase = nx * 9 + j * 0.32;
          const wave = Math.sin(phase + t * 1.5) + 0.5 * Math.sin(nx * 17 - t * 1.05);
          const y = midY + spread * band + wave * amp * env;
          const crest = (wave + 1.5) / 3; // 0..1
          const alpha = env * crest * (1 - Math.abs(spread) * 1.15) * (0.65 + lv * 0.6);
          if (alpha <= 0.02) continue;
          ctx.globalAlpha = Math.min(1, alpha);
          ctx.fillStyle = color(nx);
          const r = 1 + crest * (1.1 + lv * 1.6);
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} aria-hidden="true" />;
}
